/**
 * Spec 100 — Banner + button surfacing the spec 097 backfill route.
 *
 * Renders an amber warning banner above the rules table on /admin/rules
 * when at least one active/approved_inactive current-version rule still has
 * `embedding=NULL`. Clicking the embedded "Backfill now" button opens a
 * confirmation modal; on confirm the component POSTs
 * `/api/v1/admin/rules/embeddings/backfill` and renders a success toast
 * with the {updated, failed} counts, then re-fetches the missing-count.
 * When the count drops to 0 the banner returns null (no layout shift —
 * the page lays out without an empty placeholder).
 *
 * Returns null (banner + button absent from DOM) when the user lacks
 * `rule_admin` AND `orbit:admin` — mirrors the spec 099 button pattern
 * (no disabled-but-visible). This is the load-bearing assertion for
 * gate 100.A.3.
 *
 * `data-testid` attributes wire the `.playwright-cli/100-*.mjs` gates:
 *   - backfill-banner             (root <div>; only when count > 0)
 *   - backfill-banner-count       (span with the numeric count)
 *   - backfill-button             (trigger inside the banner)
 *   - backfill-modal              (modal root)
 *   - backfill-modal-title        (modal heading)
 *   - backfill-confirm            (modal Run-backfill button)
 *   - backfill-cancel             (modal Cancel button)
 *   - backfill-spinner-label      (only present while submitting)
 */
import { useEffect, useState } from 'react';
import { Icon } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import {
  backfillRuleEmbeddings,
  getMissingEmbeddingsCount,
} from '../api/ruleIngestion.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';

export function BackfillEmbeddingsBanner() {
  const { hasRole } = useAuth();
  const toast = useToast();

  // `null` = unknown (initial fetch still in flight). The banner only renders
  // once we have a real count — FR-13 (avoid wrong-message flash). The count
  // query is <5ms so this transient null state is brief.
  const [count, setCount] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // FR-5 — DOM-level role gate. Banner + button MUST be absent for
  // non-admins (not visibility-hidden). Gate 100.A.3 asserts
  // `page.locator('[data-testid=backfill-banner]').count() === 0`.
  const canBackfill = hasRole('rule_admin') || hasRole('orbit:admin');

  // Initial fetch + a callable refetch used after a successful backfill.
  useEffect(() => {
    if (!canBackfill) return;
    let cancelled = false;
    getMissingEmbeddingsCount()
      .then((n) => {
        if (!cancelled) setCount(n);
      })
      .catch(() => {
        // Silent on read failure — banner stays hidden rather than
        // surfacing a noisy error for a read-only signal. The admin can
        // still discover the issue indirectly via the Rescan toast.
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [canBackfill]);

  async function refetchCount() {
    try {
      const n = await getMissingEmbeddingsCount();
      setCount(n);
    } catch {
      // Same silent-failure rationale as initial fetch.
      setCount(0);
    }
  }

  function openModal() {
    if (submitting) return;
    setModalOpen(true);
  }

  function cancelModal() {
    // FR-9 — modal cannot be dismissed mid-flight; Cancel button is also
    // disabled below for the same reason.
    if (submitting) return;
    setModalOpen(false);
  }

  async function runBackfill() {
    // FR-15 — guard against double-submit when click events overlap. The
    // disabled state on the button is the primary defense; this is belt-
    // and-braces in case React schedules the click before the disabled
    // prop propagates.
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await backfillRuleEmbeddings();
      const msg = `Backfilled ${result.updated} rules · ${result.failed} failed`;
      toast.addToast(msg, 'success');
      setModalOpen(false);
      await refetchCount();
    } catch (err) {
      // FR-12 — coerce non-string messages defensively. The wrapper
      // already prefixes "Backfill failed: ..." so we render verbatim
      // when present and add the prefix otherwise.
      const raw = err instanceof Error ? err.message : String(err);
      const message = raw.startsWith('Backfill failed:')
        ? raw
        : `Backfill failed: ${raw}`;
      toast.addToast(message, 'error');
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  // Banner shape: hidden for non-admin (DOM-absent), hidden while count is
  // unknown (FR-13), hidden when no work to do (FR-11). The modal is also
  // only ever opened from inside the banner so it implicitly inherits the
  // same role gate.
  if (!canBackfill) return null;
  if (count === null) return null;
  if (count === 0) return null;

  return (
    <>
      <div
        data-testid="backfill-banner"
        role="status"
        // Spec 248 (review P1-4): Kridt's Banner, not a filled amber panel.
        className="hub-banner items-center"
        data-tone="warning"
      >
        <i className="bx bx-error hub-banner__icon" aria-hidden="true" />
        <div className="hub-banner__body">
          <p className="hub-banner__text" style={{ marginTop: 0 }}>
            <span
              data-testid="backfill-banner-count"
              className="font-semibold"
            >
              {count}
            </span>{' '}
            {count === 1 ? 'rule is' : 'rules are'} missing embeddings.
            Review-queue deduplication will miss matches against{' '}
            {count === 1 ? 'this rule' : 'these rules'} until they're
            backfilled.
          </p>
        </div>
        <Button variant="ghost" data-testid="backfill-button" onClick={openModal} disabled={submitting}>
          {submitting ? (
            <span data-testid="backfill-spinner-label">Backfilling…</span>
          ) : (
            'Backfill now'
          )}
        </Button>
      </div>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="backfill-modal-title"
          data-testid="backfill-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <h2
                id="backfill-modal-title"
                data-testid="backfill-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Backfill rule embeddings
              </h2>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                Compute embeddings for the{' '}
                <span className="font-semibold text-slate-900">{count}</span>{' '}
                {count === 1 ? 'rule' : 'rules'} that{' '}
                {count === 1 ? 'is' : 'are'} missing one. This calls the
                OpenAI embeddings API once per rule (≈ $0.0001 per rule).
              </p>
              <p>
                Safe to run repeatedly — already-embedded rules are skipped.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="ghost" data-testid="backfill-cancel" onClick={cancelModal} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" data-testid="backfill-confirm" onClick={runBackfill} disabled={submitting}>
                {submitting ? 'Backfilling…' : 'Run backfill'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
